/**
 * Network message types for Gateway WebSocket communication
 */

// ========== Client -> Gateway Messages ==========

export interface LoginMessage {
  type: 'login';
  payload: {
    account: string;
    password: string;
    /** 같은 계정으로 이미 접속 중인 세션이 있어도 강제로 종료하고 로그인한다
     *  (duplicate_login 확인 다이얼로그에서 사용자가 승인했을 때 true로 재전송). */
    force?: boolean;
  };
}

export interface SelectCharMessage {
  type: 'select_char';
  payload: {
    charName: string;
  };
}

export interface MoveMessage {
  type: 'move';
  payload: {
    x: number;
    y: number;
    heading: number;
  };
}

export interface ChatMessage {
  type: 'chat';
  payload: {
    message: string;
    channel: string;
  };
}

export interface AttackMessage {
  type: 'attack';
  payload: {
    targetId: number;
    /**
     * 대상의 타일 좌표. 서버 C_Attack이 objId와 함께 읽어 사거리를 판정한다
     * (근접 무기는 인접 타일, 활은 원거리). 빠뜨리면 서버가 패킷을 끝까지 읽지 못한다.
     */
    x: number;
    y: number;
  };
}

/**
 * 아이템 사용 요청.
 *
 * 서버 `C_UseItem`(opcode 44)은 아이템의 **use_type**에 따라 objId 뒤에 읽는
 * 바이트가 달라진다. 게이트웨이가 아이템의 use_type을 알고 채워 넣지만,
 * 클라이언트가 아는 값이 있으면 함께 보내 준다 (없으면 게이트웨이가 판단).
 *
 * ```
 * use_type 16                → readS()            : text
 * use_type 7/14/26/27/8/30   → readD()            : targetId
 * use_type 6/29              → readH() + readD()  : mapId + targetId
 * use_type 28                → readC()            : skillId
 * use_type 5/17/39           → readD() + readH()*2: targetId + x + y
 * use_type 42                → readH()*2          : x + y
 * 그 외                       → readC()            : (게이트웨이가 0을 채운다)
 * ```
 */
export interface UseItemMessage {
  type: 'use_item';
  payload: {
    itemId: number;
    /** use_type 7/8/14/26/27/30 및 5/17/39 — 대상 objId. */
    targetId?: number;
    /** use_type 6/29 — 이동 대상 맵 ID. */
    mapId?: number;
    /** use_type 5/17/39/42 — 대상 타일 좌표. */
    x?: number;
    y?: number;
    /** use_type 28 (블랭크 스크롤) — 기록할 스킬 ID. */
    skillId?: number;
    /** use_type 16 (변신 스크롤) — 입력 문자열. */
    text?: string;
    /** 클라이언트가 아이템의 use_type을 아는 경우에만 전달 (게이트웨이 힌트). */
    useType?: number;
  };
}

/**
 * 인벤토리 아이템을 바닥에 놓는다 (서버 `C_DropItem`, opcode 54).
 *
 * 서버는 `readH() x + readH() y + readD() objId + readD() count`를 읽고,
 * **좌표를 검증하지 않은 채** 그 타일의 지면 인벤토리로 아이템을 옮긴다.
 * 사거리 제한이 서버에 없으므로 클라이언트가 지켜야 한다
 * (`src/jp/l1j/server/packets/client/C_DropItem.java`).
 *
 * 서버가 거절하는 쪽은 아이템이다 — 장착 중(메시지 125), 거래 불가·봉인·펫 소환
 * 아이템(210), 여관 안(77). 거절되면 `server_message`만 오고 인벤토리는 그대로다.
 *
 * 수신 방향의 `drop_item`(S_DropItem, 바닥에 놓인 아이템 통지)과 이름이 같지만
 * 방향이 반대라 충돌하지 않는다.
 */
export interface DropItemRequestMessage {
  type: 'drop_item';
  payload: {
    /** 버릴 인벤토리 아이템의 objId. */
    itemId: number;
    /** 버릴 개수. 겹치지 않는 아이템은 1. */
    count: number;
    /** 아이템이 놓일 타일 좌표. */
    x: number;
    y: number;
  };
}

/**
 * 바닥 아이템을 줍는다 (서버 `C_PickUpItem`, opcode 188).
 *
 * 구조는 `drop_item`과 같다. **x/y는 플레이어 위치가 아니라 아이템이 놓인 타일**이며,
 * 서버가 그 좌표의 지면 인벤토리에서 objId를 찾는다. 서버는 3타일
 * (`getTileLineDistance`)을 넘으면 아무 응답 없이 무시한다.
 */
export interface PickupItemMessage {
  type: 'pickup_item';
  payload: {
    /** 주울 바닥 아이템의 objId. */
    objId: number;
    /** 주울 개수. 겹치는 아이템(아데나 등)만 의미가 있다. */
    count: number;
    /** 아이템이 놓인 타일 좌표. */
    x: number;
    y: number;
  };
}

/**
 * S_Teleport(opcode 4) 수신 후 클라이언트가 보내는 텔레포트 확인.
 * 이 응답을 보내야 서버가 실제 좌표 이동을 진행한다. 페이로드는 없다.
 */
export interface TeleportAckMessage {
  type: 'teleport_ack';
}

/**
 * NPC 대화 요청 (서버 `C_NpcTalk`, opcode 58).
 *
 * 서버 원본은 `readD()`로 objId 하나만 읽는다
 * (`src/jp/l1j/server/packets/client/C_NpcTalk.java`).
 * 응답은 `show_html`(S_NpcTalkReturn)로 오며, 대화 상대가 아닌 오브젝트를
 * 지목하면 서버가 조용히 아무 것도 보내지 않는다.
 */
export interface NpcTalkMessage {
  type: 'npc_talk';
  payload: {
    objId: number;
  };
}

/**
 * NPC 대화 선택지 실행 (서버 `C_NpcAction`, opcode 37).
 *
 * 서버 원본은 `readD()`(objId) + `readS()`(액션 문자열)을 읽는다
 * (`src/jp/l1j/server/packets/client/C_NpcAction.java`). 액션 문자열은
 * `show_html`로 받은 대화창의 링크 이름이며, 서버가 `equalsIgnoreCase`로 분기한다.
 *
 * ⚠ **`"buy"`는 플레이어가 사는 것 = NPC가 파는 목록**(`shop_sell_list`)이,
 * `"sell"`은 플레이어가 파는 것 = NPC가 사주는 목록(`shop_buy_list`)이 돌아온다.
 * 이름이 서버(NPC) 관점이라 플레이어 관점과 반대다.
 *
 * `"retrieve"`는 개인 창고 열기이며 `retrieve_list`가 돌아온다. 단 서버가
 * `npcImpl == "L1Dwarf" && level >= 5`일 때만 처리하므로 조건에 맞지 않으면
 * **아무 응답도 오지 않는다.**
 */
export interface NpcActionMessage {
  type: 'npc_action';
  payload: {
    objId: number;
    /** 대화창 링크 이름. 상점은 `"buy"` / `"sell"`, 창고는 `"retrieve"`, 그 외 NPC마다 다르다. */
    action: 'buy' | 'sell' | 'retrieve' | (string & {});
  };
}

/**
 * 상점 거래 확정의 종류 (서버 `C_Result`의 resultType).
 *
 * | 값 | 서버 주석 | 플레이어 관점 |
 * |----|-----------|---------------|
 * | 0 | アイテム購入 | 아이템 **구매** (NPC에게서 산다) |
 * | 1 | アイテム売却 | 아이템 **판매** (NPC에게 판다) |
 * | 2 | 自分の倉庫に格納 | 창고 보관 (드워프, 레벨 5 이상) |
 * | 3 | 自分の倉庫から出す | 창고 인출 (아데나 수수료가 든다) |
 */
export type ShopResultType = 0 | 1 | 2 | 3;

/**
 * `shop_result`가 실어 보내는 거래 항목 1건.
 *
 * 서버 `C_Result`는 항목마다 `readD() + readD()`(= id + count)를 읽는데,
 * **앞의 id가 resultType마다 다른 것을 가리킨다.**
 *
 * | resultType | id의 정체 |
 * |------------|-----------|
 * | 0 (구매) | `shop_sell_list` 항목의 `index` (shopItemIndex) |
 * | 1 (판매) | 인벤토리 아이템의 objId |
 * | 2 (창고 보관) | **인벤토리** 아이템의 objId |
 * | 3 (창고 인출) | **창고**(`retrieve_list`) 아이템의 objId |
 */
export interface ShopResultItem {
  /** resultType 0이면 shopItemIndex, 1·2면 인벤토리 아이템 objId, 3이면 창고 아이템 objId. */
  id: number;
  count: number;
}

/**
 * 상점 거래 확정 (서버 `C_Result`, opcode 40).
 *
 * 서버는 NPC와의 거리가 3타일을 넘으면 조용히 무시하고, `size`가 0이면
 * 아무 것도 하지 않는다 — 빈 목록을 보내지 말 것.
 */
export interface ShopResultMessage {
  type: 'shop_result';
  payload: {
    npcObjId: number;
    resultType: ShopResultType;
    items: ShopResultItem[];
  };
}

export interface PingMessage {
  type: 'ping';
}

/**
 * 계정 로그아웃이 아니라 현재 캐릭터만 로그아웃시키고 캐릭터 선택 화면으로
 * 복귀한다 (계정 세션/WebSocket 연결은 그대로 유지).
 */
export interface ReturnToCharSelectMessage {
  type: 'return_to_char_select';
}

export interface NewCharMessage {
  type: 'new_char';
  payload: {
    name: string;
    type: number;        // class id 0..6
    sex: number;         // 0=male, 1=female
    stats: number[];     // [str, dex, con, wis, cha, int] — must sum to 75
  };
}

export interface DeleteCharMessage {
  type: 'delete_char';
  payload: {
    name: string;
  };
}

export type ClientMessage =
  | LoginMessage
  | SelectCharMessage
  | NewCharMessage
  | DeleteCharMessage
  | MoveMessage
  | ChatMessage
  | AttackMessage
  | UseItemMessage
  | DropItemRequestMessage
  | PickupItemMessage
  | NpcTalkMessage
  | NpcActionMessage
  | ShopResultMessage
  | TeleportAckMessage
  | PingMessage
  | ReturnToCharSelectMessage;

// ========== Gateway -> Client Messages ==========

export interface ConnectedMessage {
  type: 'connected';
  payload: {
    message: string;
  };
}

export interface LoginResultPayload {
  result: number;
  message: string;
}

export interface LoginResultMessage {
  type: 'login_result';
  payload: LoginResultPayload;
}

export interface CharCreateResultPayload {
  result: number;  // 0=OK
  message: string;
}
export interface CharCreateResultMessage {
  type: 'char_create_result';
  payload: CharCreateResultPayload;
}

export interface CharDeleteResultPayload {
  result: number;
  message: string;
}
export interface CharDeleteResultMessage {
  type: 'char_delete_result';
  payload: CharDeleteResultPayload;
}

/**
 * CharInfo — DB에서 가져온 캐릭터 정보.
 *
 * 서버의 S_CharPacks (opcode 184) 한 개당 다음 구조를 가진다.
 *  - name, clanName (null-terminated strings)
 *  - type, sex (1 byte)
 *  - lawful, hp, mp (2 bytes / short)
 *  - ac, level (1 byte)
 *  - str, dex, con, wis, cha, int (1 byte)
 *  - isAdmin (1 byte)
 *  - birthday (4 bytes)
 */
export interface CharInfo {
  name: string;
  clanName: string;
  type: number;
  sex: number;
  lawful: number;
  hp: number;
  mp: number;
  ac: number;
  level: number;
  str: number;
  dex: number;
  con: number;
  wis: number;
  cha: number;
  int: number;
  isAdmin: boolean;
  birthday: number;
}

export interface CharListMessage {
  type: 'char_list';
  payload: CharInfo[];
}

export interface CharSpawnPayload {
  x: number;
  y: number;
  objId: number;
  gfxId: number;
  heading: number;
  hpPercent?: number;
}

/**
 * ⚠ 게이트웨이는 이 타입을 보내지 않는다.
 * `WebMessage.charSpawn()` 빌더는 있지만 `ProtocolTranslator`가 호출하지 않으며,
 * 실제 스폰은 opcode 3 → `spawn`(SpawnMessage)으로 온다.
 * CharSelectScene이 아직 구독하고 있어 남겨 둔다 (지우면 진입 경로가 흔들린다).
 */
export interface CharSpawnMessage {
  type: 'char_spawn';
  payload: CharSpawnPayload;
}

/**
 * 게이트웨이의 opcode 3 통합 스폰 표현 (PC/NPC/Pet/Door/Follower/Signboard/Summon/Doll).
 * 공통 헤더(x/y/objId/gfxId)는 항상 존재하며, type/name/heading은
 * 게이트웨이 파싱 성공 시에만 채워진다 (ProtocolTranslator.SpawnData).
 */
export interface SpawnPayload {
  x: number;
  y: number;
  objId: number;
  gfxId: number;
  /** "player" | "npc" | "monster" — statusFlags의 STATUS_PC 비트 + gfxId 분류 기반. */
  type?: string;
  /** PC는 캐릭터명, NPC/몬스터는 nameId(로케일 키) 문자열. 빈 문자열일 수 있음. */
  name?: string;
  /** 0~7 (문/보드 등 비-캐릭터는 의미 없는 값일 수 있음). */
  heading?: number;
}

export interface SpawnMessage {
  type: 'spawn';
  payload: SpawnPayload;
}

/**
 * S_OwnCharStatus (opcode 145) — 자기 캐릭터 종합 상태.
 * 인게임 진입 시 "DB 접속중..." 화면을 벗어나는 핵심 트리거.
 */
export interface OwnCharStatusPayload {
  objId: number;
  level: number;
  exp: number;
  str: number;
  intel: number;
  wis: number;
  dex: number;
  con: number;
  cha: number;
  currentHp: number;
  maxHp: number;
  currentMp: number;
  maxMp: number;
  ac: number;
  time: number;
  food: number;
  weight: number;
  lawful: number;
  fire: number;
  water: number;
  wind: number;
  earth: number;
  monsterKill: number;
}

export interface OwnCharStatusMessage {
  type: 'own_char_status';
  payload: OwnCharStatusPayload;
}

export interface CharCountMessage {
  type: 'char_count';
  payload: number;
}

export interface ObjectRemovePayload {
  objId: number;
}

export interface ObjectRemoveMessage {
  type: 'object_remove';
  payload: ObjectRemovePayload;
}

export interface ItemData {
  id: number;
  gfxId: number;
  count: number;
  name: string;
  /** 감정 여부. 게이트웨이 ItemData.identified. */
  identified?: boolean;
  /**
   * 서버 `L1Item.getUseType()`. C_UseItem이 이 값으로 itemObjId 뒤에 읽을 인자의
   * 형태를 정하므로, `use_item`을 보낼 때 그대로 되돌려 줘야 패킷 길이가 맞는다
   * (아래 UseItemMessage 주석 참조).
   */
  useType?: number;
}

export interface InventoryMessage {
  type: 'inventory';
  payload: ItemData[];
}

export interface ChatReceivePayload {
  type: string;
  sender: string;
  message: string;
}

/**
 * ⚠ 게이트웨이는 이 타입을 보내지 않는다.
 * `WebMessage.chatMessage()` 빌더는 있지만 `ProtocolTranslator`가 호출하지 않으며,
 * 서버 채팅은 전부 `server_chat`(ServerChatMessage)으로 온다.
 * 지우지 않고 두는 이유는 CharSelectScene/GameScene의 기존 구독을 흔들지 않기 위해서다.
 */
export interface ChatReceiveMessage {
  type: 'chat_message';
  payload: ChatReceivePayload;
}

export interface SysMessagePayload {
  message: string;
}

export interface SysMessage {
  type: 'sys_message';
  payload: SysMessagePayload;
}

export interface PongMessage {
  type: 'pong';
  payload: null;
}

export interface ErrorPayload {
  message: string;
}

export interface ErrorMessage {
  type: 'error';
  payload: ErrorPayload;
}

// ========== 게이트웨이 런타임 메시지 (WebMessage.java / ProtocolTranslator.java 기준) ==========

/** 핸드셰이크 중 S_ServerVersion(opcode 151) 수신 알림. */
export interface ServerVersionMessage {
  type: 'server_version';
  payload: { message: string };
}

/** 같은 계정으로 이미 접속된 세션이 있어 사용자 확인이 필요할 때. */
export interface DuplicateLoginMessage {
  type: 'duplicate_login';
  payload: { message: string };
}

/** 다른 곳에서 강제 로그인해 이 세션이 종료됐을 때. */
export interface ForceLogoutMessage {
  type: 'force_logout';
  payload: { message: string };
}

/** Opcode 14 (S_ServerMsg) — 시스템 메시지. */
export interface ServerMsgMessage {
  type: 'server_message';
  payload: { message: string };
}

/** S_MapId (opcode 150) — 맵 ID / 언더워터 플래그. */
export interface MapIdPayload {
  mapId: number;
  underwater: boolean;
}

export interface MapIdMessage {
  type: 'map_id';
  payload: MapIdPayload;
}

/** S_MoveObject (opcode 122) — PC/NPC 이동 알림 (MoveObjectData). */
export interface MoveObjectPayload {
  objId: number;
  x: number;
  y: number;
  heading: number;
  /** 항상 0x81(129) 관측 — 의미 미상, 게이트웨이가 원본 그대로 전달. */
  flag: number;
}

export interface MoveObjectMessage {
  type: 'move_object';
  payload: MoveObjectPayload;
}

/** S_HpUpdate (opcode 42) — HP 단발 갱신. */
export interface HpUpdatePayload {
  currentHp: number;
  maxHp: number;
}

export interface HpUpdateMessage {
  type: 'hp_update';
  payload: HpUpdatePayload;
}

/** S_MpUpdate (opcode 73) — MP 단발 갱신. hp_update와 대칭인 객체 형태. */
export interface MpUpdatePayload {
  currentMp: number;
  maxMp: number;
}

/**
 * S_MpUpdate (opcode 73).
 *
 * payload가 **정수 → 객체로 바뀐 계약 변경**이다. 게이트웨이를 재빌드하기 전의
 * 구버전(`WebMessage.mpUpdate(int currentMp)`)이 붙어 있을 수 있으므로 정수도
 * 계속 받는다. 정수로 오면 currentMp만 갱신하고 maxMp는 기존 값을 유지한다.
 */
export interface MpUpdateMessage {
  type: 'mp_update';
  payload: MpUpdatePayload | number;
}

/** S_Exp (opcode 121) — 레벨/경험치 갱신 (ExpData). */
export interface ExpUpdatePayload {
  level: number;
  exp: number;
}

export interface ExpUpdateMessage {
  type: 'exp_update';
  payload: ExpUpdatePayload;
}

/** S_AddItem (opcode 63) — 인벤토리 단건 추가 (AddItemData). */
export interface AddItemPayload {
  objId: number;
  magicCatalystType: number;
  useType: number;
  chargeCount: number;
  gfxId: number;
  /** statusForPacket (장착 여부 등 비트 플래그) */
  status: number;
  count: number;
  identified: boolean;
  name: string;
  /** 감정된 경우의 인챈트/속성 상태 바이트 목록 */
  itemStatus?: number[] | null;
}

export interface AddItemMessage {
  type: 'add_item';
  payload: AddItemPayload;
}

/** S_DeleteInventoryItem (opcode 148) — 인벤토리 단건 제거. */
export interface DeleteItemPayload {
  objId: number;
}

export interface DeleteItemMessage {
  type: 'delete_item';
  payload: DeleteItemPayload;
}

/**
 * 서버 채팅 채널 이름.
 *
 * 서버 `S_ChatPacket`의 type 분기(0/2/3/4/9/11/12/13/14/16)와 1:1로 대응한다.
 * 전체/혈맹/파티/트레이드/연합/위스퍼는 opcode 10(S_OPCODE_GLOBALCHAT)으로 오며,
 * 게이트웨이가 payload의 type 바이트를 보고 여기 이름을 붙여 `server_chat`으로 보낸다.
 * 게이트웨이가 아직 모르는 type이면 `"unknown_N"`이 오므로 문자열 확장을 허용해 둔다.
 */
export type ServerChatChannel =
  | 'normal'      // type 0  — 일반 (범위 채팅)
  | 'shout'       // type 2  — 외침
  | 'world'       // type 3  — 전체
  | 'clan'        // type 4  — 혈맹
  | 'party'       // type 11 — 파티
  | 'trade'       // type 12 — 트레이드
  | 'alliance'    // type 13 — 연합
  | 'chat_party'  // type 14 — 채팅 파티
  | 'whisper'     // type 9(발신 에코) / 16(수신, opcode 255)
  | (string & {}); // "unknown_N"

/** S_ChatPacket (opcode 76/255/10) — 서버가 브로드캐스트한 채팅 (ChatData). */
export interface ServerChatPayload {
  /** 원본 type 값 (0/2/3/4/9/11/12/13/14/16) */
  chatType: number;
  /** 위 ServerChatChannel 참조. 게이트웨이가 붙여 보내는 채널명. */
  channel: ServerChatChannel;
  /** objId (normal/shout/chat_party만; invisible이면 0) */
  senderId: number;
  /** whisper만 (발신자 이름) */
  senderName?: string | null;
  /**
   * 표시할 본문.
   *
   * **서버가 이미 발신자를 본문에 완성해 넣는다** (`"이름: 내용"` / `"<이름> 내용"` /
   * `"[이름] 내용"` / `"{이름} 내용"` …). 클라이언트가 발신자를 다시 붙이면
   * `"이름: 이름: 내용"`이 된다 — 본문은 그대로 출력할 것.
   */
  message: string;
  /** shout(type 2)만 */
  x?: number;
  y?: number;
}

export interface ServerChatMessage {
  type: 'server_chat';
  payload: ServerChatPayload;
}

/** S_DropItem / S_Trap (opcode 3) — 바닥 드롭 아이템 (DropItemData). */
export interface DropItemPayload {
  x: number;
  y: number;
  objId: number;
  gfxId: number;
  count: number;
  name: string;
}

export interface DropItemMessage {
  type: 'drop_item';
  payload: DropItemPayload;
}

/** S_Disconnect (opcode 95) — 서버 강제 종료 알림 (DisconnectData). */
export interface DisconnectNoticePayload {
  reasonCode: number;
  variant: string; // "default" | "id" | "unknown"
}

export interface DisconnectNoticeMessage {
  type: 'disconnect_notice';
  payload: DisconnectNoticePayload;
}

/** S_LoginGame (opcode 131) — 인게임 진입 완료 알림. */
export interface LoginToGameMessage {
  type: 'login_to_game';
  payload: { length: number };
}

/**
 * S_Teleport (opcode 4) — 텔레포트 시작.
 * 서버 원본은 `writeC(0x00) + writeC(0x40) + writeD(pc.getId())`로, 대상은 언제나
 * 자기 캐릭터다. 클라이언트는 화면을 정리한 뒤 `teleport_ack`를 되보내야 하며,
 * 그 전까지 이동 입력을 잠근다 (GameState.isTeleporting).
 */
export interface TeleportPayload {
  objId: number;
}

export interface TeleportMessage {
  type: 'teleport';
  payload: TeleportPayload;
}

/**
 * S_ItemAmount (opcode 127) — 인벤토리 단건의 개수/표시명 갱신.
 * 포션·화살처럼 쌓이는 아이템을 소비하면 이 패킷만 오고, 개수가 0이 될 때에야
 * S_DeleteInventoryItem이 온다. 반영하지 않으면 개수가 화면에 그대로 남는다.
 */
export interface ItemAmountPayload {
  objId: number;
  /** 서버 `item.getViewName()` — 개수 표기를 포함한 표시명. */
  name: string;
  count: number;
}

export interface ItemAmountMessage {
  type: 'item_amount';
  payload: ItemAmountPayload;
}

/**
 * S_NpcTalkReturn (opcode 119 = S_OPCODE_SHOWHTML) — NPC 대화창 HTML 표시.
 * htmlId는 클라이언트가 가진 HTML 리소스 이름이고, args는 그 안의 치환 인자다.
 */
export interface ShowHtmlPayload {
  objId: number;
  htmlId: string;
  args: string[];
}

export interface ShowHtmlMessage {
  type: 'show_html';
  payload: ShowHtmlPayload;
}

/**
 * S_CharVisualUpdate (opcode 113) — 캐릭터 외형 갱신.
 * weapon은 서버 `L1PcInstance.getCurrentWeapon()` (0=맨손, 무기 종류별 코드)이며
 * 장착 무기가 바뀔 때 브로드캐스트된다.
 */
export interface CharVisualUpdatePayload {
  objId: number;
  weapon: number;
}

export interface CharVisualUpdateMessage {
  type: 'char_visual_update';
  payload: CharVisualUpdatePayload;
}

/** S_Lawful (opcode 140) — 대상 오브젝트의 성향치(-32768~32767). */
export interface LawfulPayload {
  objId: number;
  lawful: number;
}

export interface LawfulMessage {
  type: 'lawful';
  payload: LawfulPayload;
}

/**
 * S_Paralysis (opcode 165) — 자기 캐릭터의 상태이상 on/off.
 * payload의 kind는 서버가 써 보내는 **원본 코드 1바이트**다 (타입+on/off가 한 값에 섞여 있다).
 *
 * | kind | 의미 |
 * |------|------|
 * | 2 / 3   | 마비 시작 / 해제 |
 * | 4 / 5   | 마비2 시작 / 해제 |
 * | 7       | 텔레포트 대기 상태 해제 |
 * | 10 / 11 | 수면 시작 / 해제 |
 * | 12 / 13 | 빙결 시작 / 해제 |
 * | 22 / 23 | 스턴 시작 / 해제 |
 * | 24 / 25 | 속박 시작 / 해제 |
 */
export interface ParalysisPayload {
  kind: number;
}

export interface ParalysisMessage {
  type: 'paralysis';
  payload: ParalysisPayload;
}

/**
 * 순간이동 북마크 1건 (서버 `S_BookmarkLoad` / `S_Bookmarks`).
 *
 * 서버 원본은 북마크마다
 * `writeH(locX) + writeH(locY) + writeS(name) + writeH(mapId) + writeD(id)`를 쓴다
 * (`src/jp/l1j/server/packets/server/S_BookmarkLoad.java`).
 *
 * **id는 `character_bookmarks.id` = 북마크 objId**이며, 순간이동 주문서
 * (use_type 6 / 29)를 쓸 때 `use_item` payload의 `targetId`로 그대로 실어야 한다.
 * 같이 보낼 `mapId`는 서버 C_UseItem이 `readH()`로 먼저 읽는 값이다.
 */
export interface BookmarkEntry {
  /** 북마크 objId (use_item의 targetId). */
  id: number;
  /** 사용자가 지정한 북마크 이름. */
  name: string;
  /** 목적지 맵 ID. */
  mapId: number;
  /** 목적지 타일 좌표. */
  x: number;
  y: number;
}

/**
 * 캐릭터의 북마크 전체 목록.
 * 로그인/맵 이동 등으로 다시 올 수 있으므로 받으면 **통째로 교체**한다.
 */
export interface BookmarksMessage {
  type: 'bookmarks';
  payload: BookmarkEntry[];
}

/**
 * S_IdentifyDesc — 확인(감정) 주문서 사용 결과 설명.
 *
 * 서버 원본(`S_IdentifyDesc.java`)은
 * `writeH(itemDescId) + writeH(messageId) + writeC(argCount) + writeS(...)*argCount` 형태로,
 * **본문 문장은 보내지 않는다**(원본 클라이언트 리소스에 있다). show_html과 같은 구조다.
 *
 * | messageId | 문장 |
 * |-----------|------|
 * | 134 | `%0: 소형 몬스터 타격 %1 대형 몬스터 타격 %2` (무기) |
 * | 135 | `%0: 방어력 %1` (방어구) |
 * | 136 | `%0: 포만도 %1 [무게 %2]` (음식) |
 * | 137 | `%0: 사용가능 횟수 %1 [무게 %2]` (완드/차지 아이템) |
 * | 138 | `%0: [무게 %1]` (그 외) |
 *
 * args[0]은 언제나 아이템 표시명이며 `$227`(축복된) / `$228`(저주받은) 접두와
 * `$숫자` 이름 키가 섞여 올 수 있다 (`utils/nameResolver.ts`로 풀 것).
 */
export interface IdentifyDescPayload {
  /** L1Item.getItemDescId() — 아이템 설명 리소스 번호. */
  descId: number;
  /** 문장 번호 (위 표). */
  messageId: number;
  /** 문장의 `%0 %1 …` 치환 인자. */
  args: string[];
}

export interface IdentifyDescMessage {
  type: 'identify_desc';
  payload: IdentifyDescPayload;
}

/** S_SkillSound (opcode 232) — 대상 위치에 스킬 이펙트(gfxId)를 재생. */
export interface SkillSoundPayload {
  objId: number;
  gfxId: number;
}

export interface SkillSoundMessage {
  type: 'skill_sound';
  payload: SkillSoundPayload;
}

/**
 * S_ChangeShape (opcode 164) — 변신.
 *
 * 서버 원본은 `writeD(objId) + writeH(polyId) + writeH(weaponTakeoff ? 0 : 29)`다
 * (`src/jp/l1j/server/packets/server/S_ChangeShape.java`).
 * **polyId 0은 변신 해제**(원래 모습으로 복귀)를 뜻한다.
 */
export interface PolyPayload {
  objId: number;
  /** 변신할 몹의 gfxId. **0이면 변신 해제.** */
  polyId: number;
  /** true면 변신과 함께 무기가 벗겨진다 (서버가 0/29 두 값으로 보내는 플래그). */
  weaponTakeoff: boolean;
}

export interface PolyMessage {
  type: 'poly';
  payload: PolyPayload;
}

/**
 * S_Invis (opcode 57) — 대상 오브젝트의 투명 상태.
 * `writeD(objId) + writeC(type)` 구조이며, **invisType이 0이 아니면 보이지 않는 상태**다.
 */
export interface InvisPayload {
  objId: number;
  /** 0 = 해제, 그 외 = 투명 (서버가 보내는 원본 코드). */
  invisType: number;
}

export interface InvisMessage {
  type: 'invis';
  payload: InvisPayload;
}

/**
 * S_Light (opcode 53) — 광원 반경.
 * `writeD(objId) + writeC(type)` 구조이며, lightSize는 시야 반경(타일)이다.
 * 램프/횃불 장착이나 라이트 마법으로 바뀐다.
 */
export interface LightPayload {
  objId: number;
  /** 시야 반경 (타일). */
  lightSize: number;
}

export interface LightMessage {
  type: 'light';
  payload: LightPayload;
}

/**
 * S_OwnCharAttrDef (opcode 15) — 방어력/속성 방어력의 **부분 갱신**.
 *
 * 서버 원본은 `writeC(ac) + writeC(fire) + writeC(water) + writeC(wind) + writeC(earth)`로
 * (`src/jp/l1j/server/packets/server/S_OwnCharAttrDef.java`) 자기 캐릭터만 대상이라 objId가 없다.
 * `own_char_status`(S_OwnCharStatus)가 싣고 오는 같은 이름의 필드들을 덮어쓰는
 * 조각 패킷이며, 장비를 바꿔 값이 변할 때 온다.
 *
 * **ac는 음수가 올 수 있다** (writeC로 나가지만 부호 있는 값 — 실측 -13).
 */
export interface AttrDefPayload {
  /** 방어력. 낮을수록 좋으며 음수가 정상이다. */
  ac: number;
  /** 속성 방어력 (own_char_status의 동명 필드와 같은 의미). */
  fire: number;
  water: number;
  wind: number;
  earth: number;
}

export interface AttrDefMessage {
  type: 'attr_def';
  payload: AttrDefPayload;
}

/**
 * S_ChangeHeading (opcode 199) — 오브젝트의 **제자리 방향 전환**.
 *
 * 서버 원본은 `writeD(objId) + writeC(heading)`이며
 * (`src/jp/l1j/server/packets/server/S_ChangeHeading.java`) 좌표가 실리지 않는다 —
 * `move_object`(S_MoveCharPacket)와 달리 위치는 그대로다.
 * NPC가 말을 건 쪽을 돌아보거나(`L1MerchantInstance`), 스킬이 시전자를 대상 쪽으로
 * 돌릴 때(`L1SkillUse`), 플레이어가 제자리 회전할 때(`C_ChangeHeading`) 온다.
 */
export interface ChangeHeadingPayload {
  objId: number;
  /** 0~7 (북쪽부터 시계 방향). 부호 없는 값이다. */
  heading: number;
}

export interface ChangeHeadingMessage {
  type: 'change_heading';
  payload: ChangeHeadingPayload;
}

/**
 * `shop_sell_list`의 항목 1건 — **NPC가 파는 물건** = 플레이어가 살 물건.
 *
 * 서버 원본은 항목마다 `writeD(i) + writeH(gfxId) + writeD(price) + writeS(name)`
 * (+ 감정 상태 바이트)를 쓴다 (`src/jp/l1j/server/packets/server/S_ShopSellList.java`).
 */
export interface ShopSellListItem {
  /**
   * 상점 목록에서의 순번(shopItemIndex). **아이템 objId가 아니다** —
   * 구매를 확정할 때 `shop_result`(resultType 0)의 `items[].id`로 이 값을 그대로 보낸다.
   */
  index: number;
  gfxId: number;
  /** 세율·배율이 이미 반영된 개당(묶음이면 묶음당) 가격. */
  price: number;
  /**
   * 표시명. 묶음 판매(packCount > 1)면 서버가 `"이름 (1000)"`처럼 **묶음 수량을**
   * 이름 꼬리에 박아 보낸다 — 인벤토리의 소지 개수와 달리 값에 포함된 정보이므로
   * 떼어내면 안 된다.
   */
  name: string;
}

/**
 * S_ShopSellList (opcode 254) — **NPC가 파는 물건 목록 = 플레이어의 "구매" 목록**.
 * `npc_action`에 `"buy"`를 보내면 이 목록이 돌아온다.
 */
export interface ShopSellListPayload {
  /** 상점 NPC의 objId. */
  objId: number;
  /** 화폐 종류 (0x00 카이모 / 0x01 진주 / **0x07 아데나**). */
  currency: number;
  items: ShopSellListItem[];
}

export interface ShopSellListMessage {
  type: 'shop_sell_list';
  payload: ShopSellListPayload;
}

/**
 * `shop_buy_list`의 항목 1건 — **NPC가 사주는 값** = 플레이어가 팔 물건.
 *
 * 서버 원본은 항목마다 `writeD(targetId) + writeD(assessedPrice)`만 쓴다
 * (`src/jp/l1j/server/packets/server/S_ShopBuyList.java`). **이름도 gfxId도 없다** —
 * 이미 플레이어가 들고 있는 아이템이라 원본 클라이언트가 인벤토리에서 찾아 붙인다.
 */
export interface ShopBuyListItem {
  /** 인벤토리 아이템의 objId (= 클라이언트 `Item.id`). */
  objId: number;
  /** 감정가(NPC가 쳐주는 값). 개당이 아니라 **소지 개수 전체의 값**이다. */
  price: number;
}

/**
 * S_ShopBuyList (opcode 170) — **NPC가 사주는 값 목록 = 플레이어의 "판매" 목록**.
 * `npc_action`에 `"sell"`을 보내면 돌아온다. 팔 수 있는 물건이 하나도 없으면
 * 서버는 이 패킷 대신 `S_NoSell`을 보낸다.
 */
export interface ShopBuyListPayload {
  /** 상점 NPC의 objId. */
  objId: number;
  /** 화폐 종류 (0x07 = 아데나). */
  currency: number;
  items: ShopBuyListItem[];
}

export interface ShopBuyListMessage {
  type: 'shop_buy_list';
  payload: ShopBuyListPayload;
}

/**
 * `retrieve_list`의 항목 1건 — **창고에 들어 있는 물건**.
 *
 * 인벤토리가 아니라 창고 쪽 objId이므로, 인출을 확정할 때
 * `shop_result`(resultType 3)의 `items[].id`로 이 값을 그대로 보낸다.
 * 반대로 보관(resultType 2)은 인벤토리 objId를 쓴다 — 두 목록의 objId를 섞으면
 * 서버가 조용히 아무 일도 하지 않는다.
 */
export interface RetrieveListItem {
  /** 창고 아이템의 objId. */
  objId: number;
  gfxId: number;
  /**
   * 감정/축복 상태 바이트. **0=축복 1=일반 2=저주 3=미감정**이고,
   * **+128이면 봉인**된 아이템이다 (상태만 볼 때는 하위 비트를 봐야 한다).
   */
  status: number;
  count: number;
  identified: boolean;
  /**
   * 표시명. 서버 원본이라 `"$326 ($11)"`처럼 이름 ID와 상태 라벨이 섞여 온다 —
   * 화면에 쓰기 전에 인벤토리·상점과 같은 규칙으로 해석해야 한다.
   */
  name: string;
}

/**
 * 창고 목록 — `npc_action`에 `"retrieve"`를 보내면 돌아온다.
 *
 * 서버는 `npcImpl == "L1Dwarf" && level >= 5`일 때만 처리하므로, 조건에 맞지
 * 않으면 **아무 응답도 보내지 않는다** (에러도 오지 않는다).
 */
export interface RetrieveListPayload {
  /** 창고 NPC의 objId. `shop_result`의 `npcObjId`로 그대로 쓴다. */
  objId: number;
  /** 창고 종류. **3 = 개인 창고** (현재 다루는 것은 이것뿐). */
  warehouseType: number;
  /** 인출에 드는 아데나 수수료. 모자라면 서버 메시지 189가 온다. */
  fee: number;
  items: RetrieveListItem[];
}

export interface RetrieveListMessage {
  type: 'retrieve_list';
  payload: RetrieveListPayload;
}

/**
 * 전투 공격 결과 (`S_AttackPacket` / `S_AttackMissPacket`, opcode 142).
 *
 * 게이트웨이는 명중/빗나감 두 패킷을 `hit` 플래그 하나로 합쳐 보낸다
 * (`ProtocolTranslator.AttackData`).
 *
 * ⚠ `damage`는 **실제 피해량이 아니다.** 원본 서버 `S_AttackPacket`은 이 자리에
 * 언제나 1을 쓴다 — 피해량은 별도의 HP 갱신 패킷으로만 알 수 있다. 따라서
 * 숫자를 띄우지 않고 명중/빗나감만 표시한다.
 */
export interface AttackResultPayload {
  /** 공격 모션/타입 코드 (원본 writeC type). */
  actionType: number;
  attackerObjId: number;
  targetObjId: number;
  /** 서버 구현상 명중이면 1, 빗나감이면 0. 피해량이 아니다. */
  damage: number;
  heading: number;
  /** true = 명중(S_AttackPacket), false = 빗나감(S_AttackMissPacket). */
  hit: boolean;
  /** 명중 패킷에서만 유효. */
  targetX: number;
  targetY: number;
  /** 0x00=없음 0x04=Claw 0x08=CounterMirror. */
  effect: number;
}

export interface AttackResultMessage {
  type: 'attack_result';
  payload: AttackResultPayload;
}

/**
 * 캐릭터 외형 이상상태 (`S_Poison`, opcode 93).
 *
 * 서버는 색만 알려준다 — 어떤 상태이상인지(독/저주/마비)는 구분해 보내지 않는다.
 */
export interface PoisonPayload {
  objId: number;
  /** 0 = 정상, 1 = 초록(독), 2 = 회색(마비 등). */
  type: number;
}

export interface PoisonMessage {
  type: 'poison';
  payload: PoisonPayload;
}

/** SP/마법저항 갱신 (`S_SpMr`, opcode 174). */
export interface SpMrPayload {
  /** 마법 공격력 보정(SP). */
  sp: number;
  /** 마법 저항(MR). */
  mr: number;
}

export interface SpMrMessage {
  type: 'sp_mr';
  payload: SpMrPayload;
}

/**
 * 능력치 상승 알림 (`S_Strup` opcode 120 / `S_Dexup` opcode 28).
 *
 * 원본 클라이언트는 이 패킷으로 능력치 상승 연출을 띄운다. 프로젝트의 서버
 * 소스에는 STR/DEX 두 종류만 존재한다.
 */
export interface StatUpPayload {
  /** `"str"` 또는 `"dex"`. */
  stat: string;
  /** 상승 연출 지속시간. */
  time: number;
  /** 상승 **후**의 능력치 값. */
  value: number;
  /** 인벤토리 무게(240 스케일). `S_Dexup`에는 없어 -1. */
  weight: number;
  /** 상승 원인/타입 코드. */
  type: number;
}

export interface StatUpMessage {
  type: 'stat_up';
  payload: StatUpPayload;
}

/** 캐릭터 칭호 (`S_CharTitle`, opcode 202). 빈 문자열이면 칭호 해제다. */
export interface CharTitlePayload {
  objId: number;
  title: string;
}

export interface CharTitleMessage {
  type: 'char_title';
  payload: CharTitlePayload;
}

/**
 * 문 열림/닫힘 (`S_Door`, opcode 35).
 *
 * objId가 아니라 **좌표**로 온다 — 문이 차지한 타일의 통행 가능 여부가 바뀐다.
 */
export interface DoorStatusPayload {
  x: number;
  y: number;
  /** 문이 놓인 방향. 0 = ／, 1 = ＼. */
  direction: number;
  /** true면 지나갈 수 있다(열림). */
  passable: boolean;
}

export interface DoorStatusMessage {
  type: 'door_status';
  payload: DoorStatusPayload;
}

/**
 * 로그인 직후 공지 (`S_CommonNews`).
 *
 * 게이트웨이는 원본 페이로드를 그대로 넘긴다. 형태가 서버 설정에 따라 달라서
 * 문자열/객체 어느 쪽이든 받을 수 있게 열어 둔다.
 */
export interface CommonNewsMessage {
  type: 'common_news';
  payload: unknown;
}

/** 날씨 (`S_Weather`). payload는 날씨 코드 정수 그 자체다. */
export interface WeatherMessage {
  type: 'weather';
  payload: number;
}

export type ServerMessage =
  | ConnectedMessage
  | LoginResultMessage
  | CharListMessage
  | CharSpawnMessage
  | SpawnMessage
  | OwnCharStatusMessage
  | CharCountMessage
  | ObjectRemoveMessage
  | InventoryMessage
  | ChatReceiveMessage
  | SysMessage
  | PongMessage
  | ErrorMessage
  | CharCreateResultMessage
  | CharDeleteResultMessage
  | ServerVersionMessage
  | DuplicateLoginMessage
  | ForceLogoutMessage
  | ServerMsgMessage
  | MapIdMessage
  | MoveObjectMessage
  | HpUpdateMessage
  | MpUpdateMessage
  | ExpUpdateMessage
  | AddItemMessage
  | DeleteItemMessage
  | ServerChatMessage
  | DropItemMessage
  | DisconnectNoticeMessage
  | LoginToGameMessage
  | TeleportMessage
  | ItemAmountMessage
  | ShowHtmlMessage
  | CharVisualUpdateMessage
  | LawfulMessage
  | ParalysisMessage
  | SkillSoundMessage
  | PolyMessage
  | InvisMessage
  | LightMessage
  | AttrDefMessage
  | ChangeHeadingMessage
  | ShopSellListMessage
  | ShopBuyListMessage
  | RetrieveListMessage
  | BookmarksMessage
  | IdentifyDescMessage
  | AttackResultMessage
  | PoisonMessage
  | SpMrMessage
  | StatUpMessage
  | CharTitleMessage
  | DoorStatusMessage
  | CommonNewsMessage
  | WeatherMessage;

// ========== Session State ==========

export enum SessionState {
  /** WebSocket 미연결 (초기 상태 / disconnect 후). */
  DISCONNECTED = 'DISCONNECTED',
  CONNECTED = 'CONNECTED',
  AUTHENTICATED = 'AUTHENTICATED',
  IN_GAME = 'IN_GAME',
}

export interface SessionInfo {
  state: SessionState;
  account: string | null;
  charName: string | null;
}
